'use client'

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, ShoppingCart, Bell, Check, Trash2, ArrowRight, Loader2, User, ChevronDown, MailOpen, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { safeRedirectTarget } from "@/lib/safe-redirect";
import { useAuth } from "@/context/auth-context";
import { useCart } from "@/context/cart-context";
import { useNotifications, type AppNotification } from "@/context/notifications-context";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { format } from "date-fns";
import { ScrollArea } from "../ui/scroll-area";
import { useTheme } from "@/context/theme-context";
import { Skeleton } from "../ui/skeleton";
import { Input } from "../ui/input";
import { useToast } from "@/hooks/use-toast";
import { CartIndicator, COUNT_BADGE, cartLabel } from "@/components/cart-indicator";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { useSiteHeaderAutoHide } from "@/hooks/use-site-header-autohide";
import { siteHeaderAutoHides } from "@/lib/site-header-autohide";

const baseNavLinks = [
  { href: "/marketplace", label: "Marketplace" },
];

export function Header() {
  const pathname = usePathname();
  // `startsWith` rather than a segment match: only /product/[id] exists under
  // this prefix, and a nested route added later would want the same treatment.
  const isProductPage = pathname?.startsWith('/product/') ?? false;
  const router = useRouter();
  const { user, authUser, isUserLoading, sellerTier, logout } = useAuth();
  const { cart } = useCart();
  const { notifications, markAsRead, markAllAsRead, removeNotification, removeAllNotifications, loadError: notificationsLoadError } = useNotifications();
  const { showNotificationBubbles, showProfilePicture } = useTheme();
  const { toast } = useToast();
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingNotifs, setDeletingNotifs] = useState<Set<string>>(new Set());
  const [expandedNotifs, setExpandedNotifs] = useState<Set<string>>(new Set());
  const [avatarLoading, setAvatarLoading] = useState(true);

  // Refs that pin the notification panel scroll position across deletions.
  // Without these the viewport snaps to the top whenever a row leaves the DOM
  // (Radix ScrollArea's underlying div rounds scrollTop to the new max if the
  // remaining content briefly fits the viewport during reflow).
  const notifViewportRef = useRef<HTMLDivElement | null>(null);
  // Continuously updated by the scroll listener — captures the user's actual
  // scroll position right up to the moment a row leaves the DOM. Reading
  // viewport.scrollTop inside the click handler gave a value that was already
  // stale by the time React committed the snapshot-listener update.
  const lastNotifScrollRef = useRef(0);
  const prevNotifCountRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // On the marketplace the header scrolls away with the page and slides back
  // in as the shopper scrolls up, and the filter bar moves up into its room
  // (src/lib/site-header-autohide.ts). It stays in while its menu or
  // notifications are open, and while focus is inside it, the search box
  // included. Not for as long as the search box is open: a typed query the
  // shopper clicks away from keeps it open, and the header would never leave.
  const headerRef = useRef<HTMLElement | null>(null);
  useSiteHeaderAutoHide(headerRef, siteHeaderAutoHides(pathname), isMobileNavOpen || isNotificationsOpen);

  useEffect(() => {
    if (isSearchVisible) searchInputRef.current?.focus();
  }, [isSearchVisible]);

  useEffect(() => {
      if (user?.avatarUrl) setAvatarLoading(true);
      else setAvatarLoading(false);
  }, [user?.avatarUrl]);

  // Show the "email not verified" indicator only when BOTH the live Firebase
  // Auth cache AND the Firestore-backed user doc agree the email is unverified.
  // The new tab opened by the magic link has a stale auth.currentUser until it
  // reloads, so using authUser.emailVerified alone briefly flashes the warning
  // even though Firestore already has emailVerified=true.
  const showUnverified = !!authUser && !authUser.emailVerified && user?.emailVerified !== true;
  const dashboardHome = sellerTier ? '/dashboard/products' : '/dashboard/library';

  const handleLogout = async () => {
    // Await the sign-out so onAuthStateChanged has fired and cleared
    // authUser / user / isUserLoading before we navigate. Otherwise the
    // destination page mounts mid-transition and downstream guards
    // (e.g. dashboard layout `!user`) flicker on the loader.
    await logout();
    router.push('/login');
  };

  const handleNotificationClick = (notif: AppNotification) => {
    // First press expands the notification to show its full text. A second
    // press on an already-expanded notification navigates to its destination.
    if (!expandedNotifs.has(notif.id)) {
        markAsRead(notif.id);
        setExpandedNotifs(prev => {
            const next = new Set(prev);
            next.add(notif.id);
            return next;
        });
        return;
    }

    const typeMapping: Record<string, string> = {
        'app': dashboardHome,
        'message': '/dashboard/messages',
        'sale': '/dashboard/products',
        'product': '/dashboard/products',
        'product_live': '/dashboard/products',
        'product_deleted': '/dashboard/library',
        'product_updated': '/dashboard/library',
        'seller_deleted': '/dashboard/library',
        'discount_end': '/dashboard/products',
        'discount_live': '/dashboard/products',
        'purchase': '/dashboard/library',
        'refund_result': '/dashboard/library?tab=refunds',
        'tier_change': '/dashboard/progression',
        'seller_verified': '/dashboard/progression',
        'identity_hold': '/seller-registration',
        'review': '/dashboard/customers',
        'review_response': '/dashboard/library',
        'refund_request': '/dashboard/customers',
        'subscription_started': '/dashboard/progression',
        'subscription_cancelled': '/dashboard/progression',
        'subscription_ended': '/dashboard/progression',
        'guest_purchase_migrated': '/dashboard/library',
        'email_share_stopped': '/dashboard/customers?tab=emails',
        'account_security': '/dashboard/settings/account',
    };

    // The server writes the whole href, query string included: a refund request
    // points at /dashboard/customers?tab=disputes. Splitting on '?' discarded that
    // tab, and because typeMapping was consulted FIRST it won outright anyway, so
    // every refund and chargeback notice landed on the default Customers tab
    // instead of the one holding the item being complained about.
    //
    // The server-authored href now wins and typeMapping is the fallback for
    // notifications stored without one. href is Admin-SDK-only (client create is
    // denied and update cannot rewrite it, see firestore.rules), and
    // safeRedirectTarget keeps this same-origin regardless.
    const targetHref = safeRedirectTarget(notif.href, typeMapping[notif.type] || dashboardHome);
    router.push(targetHref);
    setNotificationsOpen(false);
  }

  const handleDeleteNotification = async (notificationId: string) => {
    // Take a fresh reading right before kicking off the delete in case the
    // user scrolled but a scroll event hasn't fired yet (mobile inertia, etc.).
    if (notifViewportRef.current) {
        lastNotifScrollRef.current = notifViewportRef.current.scrollTop;
    }
    const targetScroll = lastNotifScrollRef.current;
    setDeletingNotifs(prev => new Set(prev).add(notificationId));
    try {
        await removeNotification(notificationId);
        // Belt-and-suspenders: the synchronous useLayoutEffect below restores
        // scroll the moment the list shrinks, but Radix ScrollArea also runs a
        // post-commit thumb-position calc that can re-clamp scrollTop. Re-pin
        // across the next two animation frames so we win that race.
        const pin = () => {
            if (notifViewportRef.current && notifViewportRef.current.scrollTop !== targetScroll) {
                notifViewportRef.current.scrollTop = targetScroll;
            }
        };
        requestAnimationFrame(() => { pin(); requestAnimationFrame(pin); });
    } catch (err) {
        console.error("Failed to delete notification:", err);
        toast({ title: "Could not delete the notification", description: "Nothing was removed. Try again.", variant: 'destructive' });
        setDeletingNotifs(prev => {
            const newSet = new Set(prev);
            newSet.delete(notificationId);
            return newSet;
        });
    }
  }

  const currentNavLinks = useMemo(() => {
    const links = [...baseNavLinks];
    // While auth/seller state is loading, withhold the Sell link entirely —
    // otherwise registered sellers see "Sell" flash in then disappear once
    // `sellerTier` resolves. The nav renders a Skeleton in its slot instead.
    if (isUserLoading) return links;
    if (!user || !sellerTier) {
        links.push({ href: "/sell", label: "Sell" });
    }
    return links;
  }, [sellerTier, user, isUserLoading]);

  // Mobile nav swaps the inline links for a hamburger + sheet, and adds an
  // explicit "Landing page" entry the desktop bar doesn't need (the wordmark
  // already links there, but on mobile users don't always read the wordmark
  // as a link).
  // Signed out, the menu also carries Log in: below sm the bar shows only
  // Sign up beside the wordmark (renderAuthControls).
  const mobileNavLinks = useMemo(() => {
    const links = [...currentNavLinks, { href: "/", label: "Landing page" }];
    if (!user && !isUserLoading) links.push({ href: "/login", label: "Log in" });
    return links;
  }, [currentNavLinks, user, isUserLoading]);
  
  // Bell-dot indicator. Keys off `read` (not `seen`) so the dot only clears
  // once the user actually presses each notification — opening the panel
  // alone no longer demotes anything from the unread state.
  const unreadAppNotifCount = useMemo(() => {
    return notifications.reduce((acc, n) => acc + (!n.read && n.type !== 'toast' ? 1 : 0), 0);
  }, [notifications]);
  const hasUnreadAppNotifications = unreadAppNotifCount > 0;

  const sortedNotifications = useMemo(() => {
    if (!notifications) return [];
    return [...notifications]
      .filter(n => n.type !== 'toast')
      .sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
  }, [notifications]);

  // Group sorted notifications into Today / Yesterday / Earlier this week / Older.
  // Buckets are local-day boundaries, computed once per render. A missing
  // createdAt (notifications that arrived before Firestore stamped server time)
  // falls into 'Older' rather than going missing.
  const groupedNotifications = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
    const startOfWeek = startOfToday - 7 * 24 * 60 * 60 * 1000;
    const groups: { key: string; label: string; items: AppNotification[] }[] = [
      { key: 'today', label: 'Today', items: [] },
      { key: 'yesterday', label: 'Yesterday', items: [] },
      { key: 'earlier-week', label: 'Earlier this week', items: [] },
      { key: 'older', label: 'Older', items: [] },
    ];
    for (const n of sortedNotifications) {
      const t = n.createdAt?.getTime();
      if (!t) { groups[3].items.push(n); continue; }
      if (t >= startOfToday) groups[0].items.push(n);
      else if (t >= startOfYesterday) groups[1].items.push(n);
      else if (t >= startOfWeek) groups[2].items.push(n);
      else groups[3].items.push(n);
    }
    return groups.filter((g) => g.items.length > 0);
  }, [sortedNotifications]);

  // Continuously track the viewport's scroll position so deletion can restore
  // the user to exactly where they were. The listener is attached when the
  // panel opens and torn down when it closes, since the viewport DOM only
  // exists while the Sheet is open.
  useEffect(() => {
    if (!isNotificationsOpen) return;
    const viewport = notifViewportRef.current;
    if (!viewport) return;
    const onScroll = () => { lastNotifScrollRef.current = viewport.scrollTop; };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => { viewport.removeEventListener('scroll', onScroll); };
  }, [isNotificationsOpen]);

  // Synchronous pin: the moment the list shrinks (a row was deleted), restore
  // the viewport scrollTop before the browser paints. Pairs with the rAF
  // re-pin in handleDeleteNotification to also catch any post-commit reflow
  // that Radix ScrollArea performs after the React commit.
  useLayoutEffect(() => {
    const count = sortedNotifications.length;
    if (count < prevNotifCountRef.current && notifViewportRef.current) {
      notifViewportRef.current.scrollTop = lastNotifScrollRef.current;
    }
    prevNotifCountRef.current = count;
  }, [sortedNotifications.length]);

  const executeSearch = () => {
      const trimmed = searchQuery.trim();
      router.push(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
      setIsSearchVisible(false);
      setSearchQuery("");
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        executeSearch();
      } else if (e.key === 'Escape') {
        // Standard search-overlay UX: Escape closes the overlay and clears
        // any in-progress query. Stops the browser from canceling other
        // pending UI state (form submits etc.) at the same time.
        e.preventDefault();
        setIsSearchVisible(false);
        setSearchQuery("");
      }
  };

  // The nav, the mobile menu and the auth controls below are JSX VALUES (the
  // last through a plain function call, since it branches), not components.
  // They used to be components declared inside Header, so every Header render
  // (each toast, each keystroke in the search box, each notification update)
  // minted a new component type, and React unmounted and remounted their whole
  // subtrees, notifications Sheet included, which threw keyboard focus out of
  // the open panel. None of them calls a hook, so none needs its own component
  // boundary; inlined, React reconciles them like any other part of Header.
  const navContent = (
    <nav className="hidden md:flex items-center gap-4 sm:gap-6 text-sm font-medium">
        {currentNavLinks.map((link) => (
          <Link
            key={link.href}
            href={link.href}
            className={cn(
              "transition-colors hover:text-foreground",
              pathname === link.href ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {link.label}
          </Link>
        ))}
        {isUserLoading && <Skeleton className="h-5 w-10" />}
    </nav>
  );

  const mobileNav = (
    <Sheet open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
          <Menu className="h-5 w-5" aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72">
        {/* Sheet still needs an accessible title for screen readers; the
            wordmark below replaces it visually. */}
        <SheetHeader className="sr-only">
          <SheetTitle>Menu</SheetTitle>
          <SheetDescription>Site navigation</SheetDescription>
        </SheetHeader>
        <Link
          href="/"
          onClick={() => setIsMobileNavOpen(false)}
          className="text-2xl font-bold"
        >
          supa<span className="text-primary">.</span>place
        </Link>
        <div className="mt-4 border-t" aria-hidden />
        <nav className="mt-4 flex flex-col">
          {mobileNavLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileNavOpen(false)}
                className={cn(
                  "rounded-md px-3 py-2 text-base font-medium transition-colors hover:bg-muted",
                  isActive ? "text-foreground bg-muted" : "text-muted-foreground"
                )}
              >
                {link.label}
              </Link>
            );
          })}
          {isUserLoading && <Skeleton className="h-9 mx-3 my-0.5" />}
        </nav>
      </SheetContent>
    </Sheet>
  );

  const renderAuthControls = () => {
    if (isUserLoading) {
      return (
        <div className="flex items-center gap-2">
            <Skeleton className="hidden sm:block h-9 w-20" />
            <Skeleton className="h-9 w-24" />
        </div>
      )
    }

    if (user) {
      return (
        <>
            <Sheet open={isNotificationsOpen} onOpenChange={(open) => {
                setNotificationsOpen(open);
                if (!open) {
                  setExpandedNotifs(new Set());
                }
            }}>
              <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                      {hasUnreadAppNotifications && showNotificationBubbles && (
                        <span aria-hidden className={COUNT_BADGE}>
                          {unreadAppNotifCount > 9 ? '9+' : unreadAppNotifCount}
                        </span>
                      )}
                      <Bell className="h-5 w-5" />
                      <span className="sr-only">
                        {hasUnreadAppNotifications
                          ? `Notifications, ${unreadAppNotifCount} unread`
                          : 'Notifications'}
                      </span>
                  </Button>
              </SheetTrigger>
              <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Notifications</SheetTitle>
                    <SheetDescription className="sr-only">Recent activity and alerts</SheetDescription>
                  </SheetHeader>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                      {/*
                        Radix Tooltip surfaces on hover *and* focus, which made
                        these labels pop the moment the Sheet opened (Radix
                        auto-focuses the first interactive child) and again
                        after every click. Native `title` is hover-only across
                        browsers and stays out of the way otherwise; aria-label
                        keeps the screen-reader story intact.
                      */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => markAllAsRead()}
                        disabled={!hasUnreadAppNotifications}
                        aria-label="Mark all as read"
                        title="Mark all as read"
                      >
                        <MailOpen className="h-4 w-4" />
                      </Button>
                      <ConfirmDialog
                        trigger={
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            disabled={notifications.filter(n => n.type !== 'toast').length === 0}
                            aria-label="Delete all notifications"
                            title="Delete all notifications"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        }
                        title="Delete all notifications?"
                        description="This will permanently remove every notification in your panel. This cannot be undone."
                        confirmLabel="Delete all"
                        cancelLabel="Keep notifications"
                        onConfirm={() => removeAllNotifications().catch((e) => {
                          console.error("Failed to delete all notifications:", e);
                          toast({ title: "Could not delete the notifications", description: "Nothing was removed. Try again.", variant: 'destructive' });
                        })}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {/* A failed listener leaves the list empty for want of a
                          read, so it must not be called "all caught up". */}
                      {unreadAppNotifCount > 0
                        ? `${unreadAppNotifCount} unread notification${unreadAppNotifCount === 1 ? '' : 's'}`
                        : notificationsLoadError
                          ? "Could not load notifications"
                          : "You're all caught up"}
                    </p>
                  </div>
                   <ScrollArea viewportRef={notifViewportRef} className="h-[calc(100%-6rem)] -mx-6 px-6 mt-2" aria-live="polite" aria-relevant="additions" aria-label="Notifications">
                      {groupedNotifications.length === 0 ? (
                           notificationsLoadError
                             ? <LoadErrorState title="Your notifications couldn't load" />
                             : <div className="text-center text-muted-foreground py-12">Orders, messages and updates will show up here.</div>
                      ) : (
                        <div className="flex flex-col">
                          {groupedNotifications.map((group, gIdx) => (
                            <Collapsible key={group.key} defaultOpen className="group/section">
                              {gIdx > 0 && <div className="border-t my-1" aria-hidden />}
                              <CollapsibleTrigger
                                className="flex w-full items-center justify-between py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <span>{group.label}</span>
                                <span className="flex items-center gap-1">
                                  <span className="text-[10px] font-normal opacity-60">{group.items.length}</span>
                                  <ChevronDown
                                    className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=closed]/section:-rotate-90"
                                    aria-hidden
                                  />
                                </span>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                                <div className="flex flex-col gap-1 pb-1">
                                  {group.items.map((notif) => {
                                    const isDeleting = deletingNotifs.has(notif.id);
                                    const isExpanded = expandedNotifs.has(notif.id);
                                    // Grey background tracks ONLY the unread state, not expansion —
                                    // a first press marks the row as read and expands it, and the
                                    // user expects the row to lose its tint at that moment so the
                                    // hover/focus feedback (a darker grey) reads as distinct.
                                    // Keys off `read`, not `seen`, so opening the panel alone does
                                    // not promote a notification out of the unread state.
                                    const isUnread = !notif.read;
                                    return (
                                      <div
                                        key={notif.id}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={isExpanded ? `Open ${notif.title}` : `Expand ${notif.title}`}
                                        onClick={() => !isDeleting && handleNotificationClick(notif)}
                                        onKeyDown={(e) => {
                                          if (isDeleting) return;
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleNotificationClick(notif);
                                          }
                                        }}
                                        className={cn(
                                          "group/notif relative flex items-start gap-3 px-3 py-2.5 rounded-md cursor-pointer",
                                          "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                                          isUnread && "bg-foreground/10",
                                          isDeleting && "opacity-40 pointer-events-none",
                                        )}
                                      >
                                        <div className="flex-grow min-w-0">
                                          <p className={cn(
                                            "text-sm leading-tight",
                                            !notif.read ? "font-semibold text-foreground" : "font-medium text-foreground/90",
                                          )}>
                                            {notif.title}
                                          </p>
                                          <p className={cn(
                                            "text-xs text-muted-foreground mt-0.5",
                                            isExpanded ? "whitespace-pre-wrap break-words" : "line-clamp-2",
                                          )}>{notif.description}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0 pl-1">
                                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                            {notif.createdAt ? format(notif.createdAt, 'h:mma').toLowerCase() : 'just now'}
                                          </span>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-muted-foreground hover:text-destructive transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteNotification(notif.id);
                                                }}
                                                disabled={isDeleting}
                                                aria-label="Delete notification"
                                              >
                                                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5"/>}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Delete</p></TooltipContent>
                                          </Tooltip>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      )}
                   </ScrollArea>
              </SheetContent>
            </Sheet>

            {showProfilePicture ? (
                <Tooltip>
                    <TooltipTrigger asChild>
                        {/* The unverified state says so in the label, not only in
                            the tooltip. The red dot is visible on every device but
                            explains nothing, and the tooltip that did explain it
                            needs a hover or a long press. */}
                        <Link
                            href={showUnverified ? "/dashboard/settings/account" : dashboardHome}
                            aria-label={showUnverified ? "Email not verified. Go to account settings to verify." : "Dashboard"}
                        >
                            <div className="relative">
                                {/* The picture, or their initial when there is none. A
                                    picture that fails to load ends the spinner too, and
                                    the initial takes its place. */}
                                <UserAvatar
                                    className="h-9 w-9 cursor-pointer"
                                    src={user?.avatarUrl}
                                    seed={authUser?.uid}
                                    name={user?.username}
                                    alt={user?.username}
                                    onLoadingStatusChange={(status) => {
                                        if (status === 'loaded' || status === 'error') setAvatarLoading(false);
                                    }}
                                />
                                {avatarLoading && !!user?.avatarUrl && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-muted">
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                                {showUnverified && (
                                    <div className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-destructive border-2 border-background" />
                                )}
                            </div>
                        </Link>
                    </TooltipTrigger>
                    {showUnverified && (
                        <TooltipContent><p>Email not verified. Click to verify.</p></TooltipContent>
                    )}
                </Tooltip>
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="relative" asChild>
                            <Link href={showUnverified ? "/dashboard/settings/account" : dashboardHome}>
                                <User className="h-5 w-5" />
                                {showUnverified && (
                                    <div className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
                                )}
                                <span className="sr-only">
                                    {showUnverified ? 'Email not verified. Go to account settings to verify.' : 'Dashboard'}
                                </span>
                            </Link>
                        </Button>
                    </TooltipTrigger>
                    {showUnverified && (
                        <TooltipContent><p>Email not verified. Click to verify.</p></TooltipContent>
                    )}
                </Tooltip>
            )}
        </>
      )
    }

    return (
      <div className="flex items-center gap-2">
          {/* Below sm there is no room for both buttons beside the wordmark;
              Log in moves into the menu there (mobileNavLinks) and Sign up,
              the one a new visitor needs, stays in the bar. */}
          <Button asChild variant="outline" className="hidden sm:inline-flex">
              <Link href="/login">Log in</Link>
          </Button>
          <Button asChild>
              <Link href="/signup">Sign up</Link>
          </Button>
      </div>
    )
  }

  return (
    <header
      ref={headerRef}
      className={cn(
        "z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        // A product page pins its OWN bar to the top instead: once the buyer
        // scrolls past the price and the buy button, the name, rating, price and
        // Add to Cart follow them down the page. Two stacked sticky bars would
        // eat a third of a phone screen, so this one gives up the top slot and
        // scrolls away. See the sticky bar in src/app/product/[id]/product-client.tsx.
        // The marketplace keeps it sticky, and the hook above sets a negative
        // `top` on it to scroll it away and bring it back.
        isProductPage ? "relative" : "sticky top-0",
      )}
    >
      <TooltipProvider>
        {/* Mobile keeps a tight gutter so the burger sits against the left wall;
            from md up the row re-inherits the 3rem inset that lines the bar up
            with the page container. */}
        <div className="flex h-16 items-center mx-2 md:-mx-[calc((100vw-100%)/2-3rem)]">
          <div className="flex items-center gap-2 sm:gap-4 md:gap-6">
            {mobileNav}
            {/* On phones too, so a phone visitor sees the name. A size smaller
                below md so it fits beside the burger, search, cart and Sign Up
                from 360px wide (measured: 12px to spare at 360). Below that,
                on a 320px phone, the bar would overflow by 28px, so it hides
                there as it always did, and it steps aside while the search box
                is open, which needs the room. */}
            <Link
              href="/"
              className={cn("text-xl md:text-2xl font-bold whitespace-nowrap max-[359px]:hidden", isSearchVisible && "max-md:hidden")}
            >
              supa<span className="text-primary">.</span>place
            </Link>
            {navContent}
          </div>

          {/* Search + cart cluster: pushed to the right by the auto margin at
              every width, so on a phone it sits with Sign Up and leaves the
              wordmark room on the left. */}
          <div className="flex items-center gap-0 md:gap-2 ml-auto">
            <div
                className={cn(
                    "relative flex items-center transition-[width] duration-300 ease-out",
                    isSearchVisible ? "w-48" : "w-9"
                )}
            >
                <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search…"
                    className={cn(
                        "h-9 w-full pr-10 transition-opacity duration-200",
                        isSearchVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                    )}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    tabIndex={isSearchVisible ? 0 : -1}
                    autoComplete="off"
                    enterKeyHint="search"
                    aria-label="Search supa.place"
                    {...(!isSearchVisible ? ({ inert: true } as any) : {})}
                    onBlur={() => { if (!searchQuery) setIsSearchVisible(false); }}
                />
                <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9"
                    onMouseDown={(e) => { if (isSearchVisible) e.preventDefault(); }}
                    onClick={() => { if (isSearchVisible) executeSearch(); else setIsSearchVisible(true); }}
                    aria-label={isSearchVisible ? "Submit search" : "Open search"}
                >
                    {isSearchVisible
                        ? <ArrowRight className="h-4 w-4" aria-hidden />
                        : <Search className="h-5 w-5" aria-hidden />}
                </Button>
            </div>

            {/* data-cart-target marks where the add-to-cart star lands, so it
                goes on the Button (the 40px box the buyer sees), not the Link
                around it. Both branches carry it; only one is ever mounted. */}
            {pathname === '/cart' ? (
              <Link href="/cart">
                <Button variant="ghost" size="icon" className="relative" data-cart-target>
                    <CartIndicator hasItems={cart.length > 0} count={cart.length} />
                    <ShoppingCart className="h-5 w-5" />
                    <span className="sr-only">{cartLabel(cart.length)}</span>
                </Button>
              </Link>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                   <Link href="/cart">
                      <Button variant="ghost" size="icon" className="relative" data-cart-target>
                          <CartIndicator hasItems={cart.length > 0} count={cart.length} />
                          <ShoppingCart className="h-5 w-5" />
                          <span className="sr-only">{cartLabel(cart.length)}</span>
                      </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent><p>View cart</p></TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Notifications + avatar (or Sign Up) trail the search/cart cluster
              against the right wall. */}
          <div className="flex items-center gap-0 md:gap-2 ml-1 md:ml-2 shrink-0">
            {renderAuthControls()}
          </div>
        </div>
      </TooltipProvider>
    </header>
  );
}
